// src/app/pages/app/DashboardPage.jsx
import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  TrendingUp, 
  ArrowRight, 
  Activity, 
  Award,
  Zap,
  Shield,
  Globe,
  BarChart3,
  Wallet,
  Sparkles,
  ChevronRight,
  Star,
  Rocket,
  Crown
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getTradeRequests } from "../../features/trade/tradeApi.js";

function normalizeStatus(s) {
  const x = String(s || "").toUpperCase();
  if (x.includes("PENDING")) return "PENDING";
  if (x.includes("APPROVED")) return "APPROVED";
  if (x.includes("REJECTED")) return "REJECTED";
  return "OTHER";
}

export default function DashboardPage() {
  const nav = useNavigate();

  const reqQ = useQuery({
    queryKey: ["tradeRequests"],
    queryFn: () => getTradeRequests(),
    staleTime: 3_000,
    retry: 1,
  });

  const requests = useMemo(() => {
    const d = reqQ.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.items)) return d.items;
    return [];
  }, [reqQ.data]);

  const recent = useMemo(() => {
    const sorted = [...requests].sort((a, b) => {
      const ta = new Date(a?.createdAt || a?.created_at || 0).getTime();
      const tb = new Date(b?.createdAt || b?.created_at || 0).getTime();
      return tb - ta;
    });

    return sorted.slice(0, 5).map((r) => ({
      id: r?.id,
      market: r?.market,
      dir: r?.direction,
      stake: r?.stakeAmount,
      status: normalizeStatus(r?.status),
      createdAt: r?.createdAt || r?.created_at,
    }));
  }, [requests]);

  return (
    <div className="min-h-screen p-6 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      {/* Hero Section - Glowing Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-950/80 via-purple-950/60 to-cyan-950/40 border border-indigo-500/20 p-8 md:p-12 mb-8 shadow-2xl shadow-indigo-500/10">
        {/* Animated Background Glows */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-cyan-500/20 to-emerald-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-purple-600/10 to-pink-600/10 rounded-full blur-2xl" />
        
        {/* Floating Particles */}
        <div className="absolute top-10 right-20 w-2 h-2 bg-indigo-400 rounded-full animate-ping" />
        <div className="absolute bottom-20 left-32 w-3 h-3 bg-cyan-400 rounded-full animate-ping delay-300" />
        <div className="absolute top-1/3 right-1/4 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping delay-700" />
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Sparkles size={22} className="text-white" />
            </div>
            <div>
              <span className="text-xs font-semibold text-indigo-300 bg-indigo-500/20 px-3 py-1 rounded-full border border-indigo-500/30">
                🚀 Welcome Back
              </span>
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Trade Smarter.
            <br />
            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Win Bigger.
            </span>
          </h1>
          <p className="text-gray-300 text-lg max-w-xl mb-8 leading-relaxed">
            Access live global markets, manage your portfolio, and execute trades with lightning speed — all from one powerful platform.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => nav("/app/trade")}
              className="group px-8 py-3.5 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-600 hover:from-indigo-700 hover:via-purple-700 hover:to-cyan-700 text-white font-semibold flex items-center gap-2 transition-all hover:scale-[1.02] shadow-lg shadow-indigo-500/25"
            >
              Start Trading <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => nav("/app/requests")}
              className="px-8 py-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold transition-all hover:scale-[1.02] backdrop-blur-sm"
            >
              View History
            </button>
          </div>

          {/* Mini Stats */}
          <div className="mt-8 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-gray-400">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span>Live Markets</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Shield size={14} className="text-indigo-400" />
              <span>Secure Trading</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Zap size={14} className="text-yellow-400" />
              <span>Low Latency</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions - Glass Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {[
          {
            title: "Trade Now",
            description: "Access live markets instantly",
            icon: Zap,
            gradient: "from-indigo-600 to-blue-600",
            bgGradient: "from-indigo-500/10 to-blue-500/5",
            iconBg: "bg-indigo-500/20",
            onClick: () => nav("/app/trade")
          },
          {
            title: "Your Wallet",
            description: "Track your assets and balances",
            icon: Wallet,
            gradient: "from-purple-600 to-pink-600",
            bgGradient: "from-purple-500/10 to-pink-500/5",
            iconBg: "bg-purple-500/20",
            onClick: () => nav("/app/wallet")
          },
          {
            title: "Trade History",
            description: "Review all your transactions",
            icon: BarChart3,
            gradient: "from-orange-600 to-amber-600",
            bgGradient: "from-orange-500/10 to-amber-500/5",
            iconBg: "bg-orange-500/20",
            onClick: () => nav("/app/requests")
          },
        ].map((action) => (
          <button
            key={action.title}
            onClick={action.onClick}
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 p-6 text-left hover:border-gray-700 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-black/30"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${action.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
            <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${action.gradient} opacity-5 rounded-full blur-2xl group-hover:opacity-15 transition-opacity duration-300`} />
            <div className="relative z-10">
              <div className={`h-12 w-12 rounded-xl ${action.iconBg} flex items-center justify-center mb-4`}>
                <action.icon size={20} className={`bg-gradient-to-br ${action.gradient} bg-clip-text text-transparent`} />
              </div>
              <h3 className="text-white font-semibold text-lg mb-1">{action.title}</h3>
              <p className="text-gray-400 text-sm">{action.description}</p>
              <div className="mt-3 flex items-center text-xs font-medium text-gray-500 group-hover:text-gray-300 transition-colors">
                Get Started <ChevronRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Features & Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Features */}
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Crown size={16} className="text-yellow-500" />
              <h3 className="text-white font-semibold">Premium Features</h3>
            </div>
            <div className="space-y-4">
              {[
                { icon: Globe, title: "Global Markets", desc: "Crypto, FX, Metals & more" },
                { icon: TrendingUp, title: "Real-time Data", desc: "Live pricing & charts" },
                { icon: Shield, title: "Secure Trading", desc: "Military-grade protection" },
                { icon: Rocket, title: "Lightning Speed", desc: "< 1ms latency execution" },
              ].map((feature) => (
                <div key={feature.title} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                  <div className="h-8 w-8 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon size={14} className="text-primary-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">{feature.title}</div>
                    <div className="text-xs text-gray-400">{feature.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-indigo-950/30 to-purple-950/30 border border-indigo-500/20 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Star size={16} className="text-yellow-500" />
              <span className="text-xs font-semibold text-indigo-300">Pro Tip</span>
            </div>
            <p className="text-sm text-gray-300">
              Start with a small trade to test the waters. Use our demo mode to practice risk-free.
            </p>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 overflow-hidden h-full">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Activity size={16} className="text-cyan-400" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">Recent Activity</div>
                  <div className="text-xs text-gray-400">Your latest trade requests</div>
                </div>
              </div>
              <button
                onClick={() => nav("/app/requests")}
                className="text-sm text-primary-400 hover:text-primary-300 font-semibold flex items-center gap-1 transition-colors"
              >
                View all <ArrowRight size={14} />
              </button>
            </div>

            {reqQ.isError && (
              <div className="px-6 py-4 text-sm text-red-300 bg-red-500/10 border-t border-red-500/20">
                Failed to load trade requests: {reqQ.error?.message || "Unknown error"}
              </div>
            )}

            <div className="divide-y divide-gray-800">
              {recent.map((r) => (
                <div key={r.id} className="px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors group">
                  <div className="flex items-center gap-4">
                    <div className={`h-2 w-2 rounded-full ${
                      r.status === "APPROVED" ? "bg-emerald-400" :
                      r.status === "PENDING" ? "bg-amber-400" :
                      "bg-red-400"
                    }`} />
                    <div>
                      <div className="text-sm font-semibold text-white flex items-center gap-2">
                        {r.market}
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          r.dir === "UP" ? "text-green-400 bg-green-500/10" : "text-red-400 bg-red-500/10"
                        }`}>
                          {r.dir}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400">
                        Stake: <span className="text-gray-300">{r.stake}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`text-xs font-bold px-3 py-1 rounded-lg border ${
                      r.status === "APPROVED"
                        ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/20"
                        : r.status === "PENDING"
                        ? "text-amber-300 bg-amber-500/10 border-amber-500/20"
                        : "text-red-300 bg-red-500/10 border-red-500/20"
                    }`}
                  >
                    {r.status}
                  </div>
                </div>
              ))}

              {!reqQ.isLoading && recent.length === 0 && (
                <div className="px-6 py-12 text-center">
                  <div className="text-5xl mb-4">📊</div>
                  <p className="text-gray-400 font-medium">No activity yet</p>
                  <p className="text-xs text-gray-500 mt-1">Start trading to see your activity here</p>
                  <button
                    onClick={() => nav("/app/trade")}
                    className="mt-4 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
                  >
                    Place Your First Trade
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
